import React, { useEffect, useState } from "react";
import { usePage, InertiaLink } from "@inertiajs/inertia-react";
import { useAtom } from "jotai";
import { DynamicInputType } from "@/Components/Questions/InputTypes/Create";
import { Inertia } from "@inertiajs/inertia";
import {
  LabeledInput,
  LabeledMultiSelect,
  LabeledSelect,
} from "@/Components/Form";
import { QUESTION_REPORT_TYPES } from "@/Enums/question_report_types";
import { QUESTION_TYPES } from "@/Enums/question_types";
import { Info } from "@/Components/Info";
import { Formheader } from "@/Components";
import { Trash } from "react-feather";
import { capitalize } from "lodash";
import EmissionFactorExplorerSidebar from "@/Components/Team/EmissionFactorExplorerSidebar";
import { LoadingProvider } from "@/Contexts/LoadingContext";
import { atom } from "jotai/index";
import AddTeamsToActivitySidebar from "@/Pages/Organization/Library/Partials/AddTeamsToActivitySidebar";

export const isEmissionFactorExplorerOpenAtom = atom(null);

export function QuestionBaseForm({
  atom,
  showReporterSelect = true,
  showAddTeams = false,
}) {
  const tags = usePage().props?.tags || {};
  // const budgetTags = usePage().props?.budgetTags || {};
  const budgetTags = { data: tags.data.filter((tag) => tag.budget) || [] };
  // const budgetTags = Object.fromEntries(Object.entries(tags.data).filter(([key, value]) => value.budget) ) || {};

  const errors = usePage().props?.errors || {};
  const team = usePage().props?.team || {};
  const questionTypes = usePage().props?.questionTypes?.data || [];
  const questionType = usePage().props?.questionType?.data || {};
  const teams = usePage().props?.teams || {};
  // const typesWithBudgetCategory = [
  //   QUESTION_TYPES.toInt(QUESTION_TYPES.Select),
  //   QUESTION_TYPES.toInt(QUESTION_TYPES.Calculate),
  // ];

  const [isAddTeamsOpen, setIsAddTeamsOpen] = useState(false);
  const [isGhgCategoryVisible, setIsGhgCategoryVisible] = useState(false);

  const [payload, setPayload] = useAtom(atom);

  useEffect(() => {
    const has_emission_factor = payload?.inputs?.some(
      (i) => i.emission_factor_id,
    );
    setIsGhgCategoryVisible(
      QUESTION_TYPES.toInt(QUESTION_TYPES.Calculate) ===
        Number(payload.question_type_id) ||
        (has_emission_factor &&
          QUESTION_TYPES.toInt(QUESTION_TYPES.Select) ===
            Number(payload.question_type_id)),
    );
  }, [payload.question_type_id, payload.inputs]);

  useEffect(() => {
    if (!questionTypes?.length) {
      Inertia.reload({ only: ["questionTypes"] });
    }

    if (!payload?.id && !payload.question_type_id) {
      setPayload((prev) => ({
        ...prev,
        question_type_id: questionType?.id || 1,
      }));
    }
  }, []);

  const errorBag = payload.errorBag;

  const userOptions =
    team?.data?.users.map((u) => ({ value: u.id, label: u.fullname })) || [];

  const defaultResultTypeOptions = [
    QUESTION_REPORT_TYPES.Total,
    QUESTION_REPORT_TYPES.Latest,
  ];

  const questionSpecificResultTypeOptions = {
    [QUESTION_TYPES.toInt(QUESTION_TYPES.Calculate)]: [
      ...defaultResultTypeOptions,
      QUESTION_REPORT_TYPES.Average,
    ],
    [QUESTION_TYPES.toInt(QUESTION_TYPES.Select)]: defaultResultTypeOptions,
    [QUESTION_TYPES.toInt(QUESTION_TYPES.FreeText)]: defaultResultTypeOptions,
    [QUESTION_TYPES.toInt(QUESTION_TYPES.Distribute)]: defaultResultTypeOptions,
  };

  const resultTypeOptions = (
    questionSpecificResultTypeOptions[payload?.question_type_id] || []
  ).map((key) => ({
    value: key,
    label: capitalize(key),
  }));

  const handleFormUpdate = (e) => {
    const { name, value } = e.target;
    setPayload({ ...payload, [name]: value });
  };

  function handleDeleteTeam(itemKey) {
    setPayload((prevPayload) => ({
      ...prevPayload,
      teams: prevPayload.teams.filter((t) => t !== itemKey),
    }));
  }

  return (
    <>
      <div className="mt-8 w-full bg-gray-light">
        <div className="mb-4 w-full">
          <div className="bg-gray-white p-4 pt-2 md:p-8 md:pt-4">
            <Formheader>Activity details</Formheader>

            <LabeledInput
              name="title"
              title="Name the activity*"
              placeholder="Enter name"
              className="mb-4"
              value={payload.title}
              onChange={handleFormUpdate}
              error={errors[errorBag]?.title}
            />

            {questionTypes?.map((block) =>
              Number(payload.question_type_id) === block.id
                ? DynamicInputType(block, {
                    atom: atom,
                    errorBag: errorBag,
                  })
                : null,
            )}
          </div>
        </div>

        <div className="mb-4 w-full">
          <div className="bg-gray-white p-4 pt-2 md:p-8 md:pt-4">
            <Formheader>Reporting and results</Formheader>

            <div className="my-4 flex w-full gap-4 max-md:flex-wrap">
              <LabeledSelect
                name="result_type"
                title="Data displayed in Results"
                options={resultTypeOptions}
                icon={`dashboard-result dashboard-result--${
                  payload?.result_type || QUESTION_REPORT_TYPES.Total
                }`}
                onChange={handleFormUpdate}
                defaultValue={
                  payload?.result_type || QUESTION_REPORT_TYPES.Total
                }
                error={errors[errorBag]?.result_type}
                containerClass="grow"
              />
              {showReporterSelect && (
                <LabeledSelect
                  name="user_id"
                  title="Choose reporter"
                  options={userOptions}
                  icon="user"
                  onChange={handleFormUpdate}
                  defaultValue={payload?.user_id || ""}
                  error={errors[errorBag]?.user_id}
                  containerClass="grow"
                />
              )}
            </div>

            {(Number(payload?.question_type_id) ===
              QUESTION_TYPES.toInt(QUESTION_TYPES.Calculate) ||
              (Number(payload?.question_type_id) ===
                QUESTION_TYPES.toInt(QUESTION_TYPES.Select) &&
                payload?.inputs?.some((i) => i.emission_factor_id))) && (
              <>
                <p>Include data in your CO2 budget and emission inventory*</p>

                <div className="my-4 flex flex-wrap gap-4">
                  <label htmlFor="question_exclude_from_budget_yes">
                    <input
                      checked={!payload?.exclude_from_budget}
                      type="radio"
                      name="exclude_from_budget"
                      id="question_exclude_from_budget_yes"
                      className="mr-2"
                      onChange={(e) =>
                        handleFormUpdate({
                          target: {
                            name: "exclude_from_budget",
                            value: !e.target.checked,
                          },
                        })
                      }
                    />
                    <span>Yes, include</span>
                  </label>

                  <label htmlFor="question_exclude_from_budget_no">
                    <input
                      checked={payload?.exclude_from_budget}
                      type="radio"
                      name="exclude_from_budget"
                      id="question_exclude_from_budget_no"
                      className="mr-2"
                      onChange={(e) =>
                        handleFormUpdate({
                          target: {
                            name: "exclude_from_budget",
                            value: e.target.checked,
                          },
                        })
                      }
                    />
                    <span>No, exclude</span>
                  </label>
                </div>
              </>
            )}

            <div className="mb-4 flex w-full flex-col flex-nowrap justify-evenly gap-4 md:flex-row">
              {isGhgCategoryVisible && (
                <div className="relative basis-full md:basis-1/3">
                  <LabeledSelect
                    title="Choose a GHG-category"
                    options={
                      budgetTags?.data?.map((t) => ({
                        label: t.name,
                        value: t.id,
                      })) || []
                    }
                    value={payload?.meta?.budget_category || 0}
                    onChange={(e) => {
                      return handleFormUpdate({
                        target: {
                          name: "meta",
                          value: {
                            ...payload.meta,
                            budget_category: e.target.value,
                          },
                        },
                      });
                    }}
                    icon="budget"
                    placeholder="Choose a category"
                  />
                  <Info containerClass="absolute top-1 right-1">
                    The GHG-category will be used to accumulate emissions in
                    your CO2-budget and in Greenhouse Gas Emissions Inventory
                  </Info>
                </div>
              )}

              <LabeledMultiSelect
                title="Choose categories"
                options={
                  tags?.data.map((t) => ({ label: t.name, value: t.id })) || []
                }
                value={payload.tags || []}
                yellowValues={true}
                onChange={(items) =>
                  handleFormUpdate({ target: { name: "tags", value: items } })
                }
                placeholder="Choose or create a new category"
                containerClass="flex-grow"
              />
            </div>
          </div>
        </div>
      </div>
      {showAddTeams && (
        <div className="mb-4 flex flex-wrap bg-gray-light px-8 py-4">
          <div className="w-full">
            <Formheader>Teams</Formheader>
          </div>

          {teams &&
            payload?.teams &&
            payload.teams.map((team_id) => {
              const team = teams.data.find((t) => t.id === team_id);
              return (
                <div
                  key={team_id}
                  className="mb-2 flex w-full justify-between border border-gray-lighter bg-white px-4 py-2 shadow-sm"
                >
                  <div>
                    <InertiaLink
                      href={`/teams/${team_id}/activity/${payload?.id}`}
                      className="hover:underline"
                    >
                      <span className="font-bold">{team?.name}</span>
                    </InertiaLink>
                  </div>

                  <span className="hover-circle relative">
                    <Trash
                      className="cursor-pointer"
                      onClick={() => handleDeleteTeam(team.id)}
                    />
                  </span>
                </div>
              );
            })}

          <AddTeamsToActivitySidebar
            atom={atom}
            openModal={isAddTeamsOpen}
            setOpenModal={setIsAddTeamsOpen}
          />

          <div className="mb-4 w-full">
            {teams.data.length > 0 && (
              <button
                type="button"
                className="btn-blue mt-2"
                onClick={() => {
                  setIsAddTeamsOpen(!isAddTeamsOpen);
                }}
              >
                Add teams
              </button>
            )}
          </div>
        </div>
      )}
      <LoadingProvider>
        <EmissionFactorExplorerSidebar atom={atom} />
      </LoadingProvider>
    </>
  );
}
